import { useCallback, useEffect, useState } from 'react';
import type { GroupCreationRequest } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useIsPlatformAdmin } from '../../auth/hooks/usePermissions';
import {
  subscribeToGroupCreationRequests,
  subscribeToUserGroupCreationRequests,
} from '../api/groupCreationRequestRepository';

export type GroupCreationRequestsState = {
  readonly requests: GroupCreationRequest[];
  readonly loading: boolean;
  readonly error: Error | null;
};

/**
 * Hook to manage group creation request state with real-time updates
 *
 * Behavior:
 * - Platform admins: Subscribe to ALL requests (optionally filtered by status)
 * - Regular users: Subscribe only to their own requests
 * - Automatically handles cleanup on unmount
 *
 * @param statusFilter Optional filter for request status (only applies to admin view)
 * @returns State object with requests, loading state, and error state
 */
export const useGroupCreationRequests = (
  statusFilter?: 'pending' | 'approved' | 'rejected',
): GroupCreationRequestsState => {
  const { firestore } = useFirebaseServices();
  const { user } = useAuth();
  const isPlatformAdmin = useIsPlatformAdmin();

  const [requests, setRequests] = useState<GroupCreationRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const handleSnapshot = useCallback((snapshotRequests: GroupCreationRequest[]) => {
    setRequests(snapshotRequests);
    setLoading(false);
  }, []);

  const handleError = useCallback((subscriptionError: Error) => {
    setError(subscriptionError);
    setLoading(false);
  }, []);

  useEffect(() => {
    // If no user is authenticated, clear state and don't subscribe
    if (!user) {
      setRequests([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Platform admins see all requests (with optional status filter)
    // Regular users see only their own requests
    const unsubscribe = isPlatformAdmin
      ? subscribeToGroupCreationRequests(
          firestore,
          handleSnapshot,
          handleError,
          statusFilter,
        )
      : subscribeToUserGroupCreationRequests(
          firestore,
          user.uid,
          handleSnapshot,
          handleError,
        );

    return () => {
      unsubscribe();
    };
  }, [firestore, user, isPlatformAdmin, statusFilter, handleSnapshot, handleError]);

  return {
    requests,
    loading,
    error,
  };
};
